'use client';

import { useCallback } from 'react';

import { useTranslations } from 'next-intl';

import PoolsHistoryCard from '@/components/global/PoolsHistoryCard';
import TransferHistoryCard from '@/components/global/TransferHistoryCard';
import { TransferHistoryCardWithRefund } from '@/components/global/TransferHistoryCard/TransferHistoryCardWithRefund';
import { Image } from '@/components/shared/Image';
import { H2, P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import {
  I_UserPoolTransaction,
  I_UserPoolTransactionPool
} from '@/server/actions/user/getUserPoolTransactions.action';
import { C_DECIMAL_PLACES } from '@/ts/constants/C_DECIMAL_PLACES';
import {
  T_UserBlockchainData,
  T_UserPredictionEventParticipation
} from '@/ts/types/T_UserBlockchainData';

import { I_TransformedPredictionItem } from '../hooks/usePredictionHistoryTransformer';

interface I_HistoryListProps {
  variant: 'predictions' | 'pools';
  transformedPredictionData: I_TransformedPredictionItem[];
  poolTransactionsData?: I_UserPoolTransaction[];
  poolsData?: I_UserPoolTransactionPool[];
  isScrolled: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isLoadingPredictions: boolean;
  hasMorePredictions: boolean;
  isLoadingPoolTransactions?: boolean;
  hasMorePoolTransactions?: boolean;
  blockchainData?: T_UserBlockchainData;
  onLoadMore: () => void;
  onLoadMorePoolTransactions?: () => void;
  onClaimWin: (participation: T_UserPredictionEventParticipation) => void;
  onClaimRefund: (participation: T_UserPredictionEventParticipation) => void;
  onViewWinners: (item: I_TransformedPredictionItem) => void;
  onViewMissedPrediction: (item: I_TransformedPredictionItem) => void;
  onViewOngoing: (item: I_TransformedPredictionItem) => void;
}

export function HistoryList({
  variant,
  transformedPredictionData,
  poolTransactionsData,
  poolsData,
  isScrolled,
  scrollRef,
  isLoadingPredictions,
  hasMorePredictions,
  isLoadingPoolTransactions,
  hasMorePoolTransactions,
  onLoadMore,
  onLoadMorePoolTransactions,
  onClaimWin,
  onClaimRefund,
  onViewWinners,
  onViewMissedPrediction,
  onViewOngoing
}: I_HistoryListProps) {
  const t = useTranslations();
  const isPredictions = variant === 'predictions';
  const isPools = variant === 'pools';

  const historyList = useCallback(() => {
    if (isPredictions) {
      if (!transformedPredictionData.length) {
        return null;
      }

      return transformedPredictionData.map((item, idx: number) => {
        const {
          participation,
          Icon,
          tokenIconUrl,
          tokenIconAlt,
          tokenSymbol,
          showPredictionsLabel,
          predictedPrice,
          dateTime,
          status,
          wonAmountText,
          lostAmountText,
          refundAmountText,
          isClaimWin,
          isClaimRefund,
          isClaimed,
          isClaimedRefund,
          showEyeIcon,
          isCurrentlyClaiming
        } = item;

        // Use wrapper component for canceled predictions to fetch refund from contract
        const CardComponent =
          status === 'canceled'
            ? TransferHistoryCardWithRefund
            : TransferHistoryCard;

        return (
          <CardComponent
            key={participation.id || idx}
            Icon={Icon}
            tokenIconUrl={tokenIconUrl}
            tokenIconAlt={tokenIconAlt}
            tokenSymbol={tokenSymbol}
            showPredictionsLabel={showPredictionsLabel}
            predictedPrice={predictedPrice}
            dateTime={dateTime}
            status={status}
            wonAmountText={wonAmountText}
            lostAmountText={lostAmountText}
            refundAmountText={refundAmountText}
            isClaimWin={isClaimWin}
            isClaimRefund={isClaimRefund}
            isClaimed={isClaimed}
            isClaimedRefund={isClaimedRefund}
            isClaiming={isCurrentlyClaiming}
            showEyeIcon={showEyeIcon}
            participation={status === 'canceled' ? participation : undefined}
            onClaimWin={() => onClaimWin(participation)}
            onClaimRefund={() => onClaimRefund(participation)}
            onViewWinners={() => onViewWinners(item)}
            onViewMissedPrediction={() => onViewMissedPrediction(item)}
            onViewOngoing={() => onViewOngoing(item)}
          />
        );
      });
    }

    if (isPools) {
      // Use dynamic pool transactions data if available
      if (poolTransactionsData && poolTransactionsData.length > 0) {
        return poolTransactionsData.map((transaction, idx: number) => {
          const isBuy = transaction.type === 'BUY';
          const tokenAmount = parseFloat(transaction.tokenAmountDecimal || '0');
          const priceDecimal = parseFloat(transaction.priceDecimal || '0');
          const usdcAmount = parseFloat(transaction.usdcAmountDecimal || '0');
          const feeAmount = parseFloat(transaction.feeDecimal || '0');
          const totalCost = usdcAmount + feeAmount;

          // Find matching pool to get current price
          const matchingPool = poolsData?.find(
            pool =>
              pool.poolAddress.toLowerCase() ===
              transaction.poolAddress.toLowerCase()
          );
          const currentPrice = parseFloat(
            matchingPool?.currentPriceDecimal || priceDecimal.toString()
          );
          const marketValue = tokenAmount * currentPrice;

          // Calculate profit percentage: ((currentPrice - priceDecimal) / priceDecimal) * 100
          let profitPercent = 0;
          if (priceDecimal > 0 && isBuy) {
            profitPercent =
              ((currentPrice - priceDecimal) / priceDecimal) * 100;
          }

          return (
            <PoolsHistoryCard
              key={transaction.id || idx}
              type={isBuy ? 'buy' : 'sell'}
              boughtAt={formatNumberWithCommasAndDecimals(
                priceDecimal.toString(),
                C_DECIMAL_PLACES
              )}
              tradingPair={`USDC/${transaction.poolTokenSymbol}`}
              token={transaction.poolTokenSymbol}
              tokens={formatNumberWithCommasAndDecimals(
                transaction.tokenAmountDecimal,
                C_DECIMAL_PLACES
              )}
              totalCost={formatNumberWithCommasAndDecimals(
                totalCost.toString(),
                C_DECIMAL_PLACES
              )}
              marketValue={formatNumberWithCommasAndDecimals(
                marketValue.toString(),
                C_DECIMAL_PLACES
              )}
              profit={profitPercent}
              blockTimestamp={transaction.blockTimestamp}
            />
          );
        });
      }

      // No transactions available
      return null;
    }
  }, [
    isPredictions,
    isPools,
    transformedPredictionData,
    poolTransactionsData,
    poolsData,
    onClaimWin,
    onClaimRefund,
    onViewWinners,
    onViewMissedPrediction,
    onViewOngoing
  ]);

  return (
    <div className="relative">
      {isScrolled && (
        <div className="hidden md:block absolute -top-2.5 left-0 w-full h-21">
          <Image
            src="/assets/images/scrollMask.png"
            alt="Slider mask end"
            fill
            className="object-cover"
          />
        </div>
      )}
      <div
        ref={scrollRef}
        className="flex flex-col gap-4 overflow-y-auto max-h-none md:max-h-300"
      >
        {isPredictions &&
        isLoadingPredictions &&
        !transformedPredictionData.length ? (
          <div className="flex justify-center items-center py-8">
            <Spinner variant="circle" size={24} />
          </div>
        ) : isPools &&
          isLoadingPoolTransactions &&
          !poolTransactionsData?.length ? (
          <div className="flex justify-center items-center py-8">
            <Spinner variant="circle" size={24} />
          </div>
        ) : isPredictions &&
          !isLoadingPredictions &&
          !transformedPredictionData.length ? (
          <div className="flex justify-center items-center py-8 flex-col">
            <Image
              width={200}
              height={200}
              alt={t('pools.title')}
              src="/assets/images/account/predictions.png"
              className="max-md:w-32 sm:w-50"
            />
            <H2 className="mb-3">
              {t('profile.predictionWinners.noPredictionsYet')}
            </H2>
            <P>{t('profile.predictionWinners.noPredictionsYetDesc')}</P>
          </div>
        ) : isPools &&
          !isLoadingPoolTransactions &&
          !poolTransactionsData?.length ? (
          <div className="flex justify-center items-center py-8 flex-col">
            <Image
              width={200}
              height={200}
              alt={t('pools.title')}
              src="/assets/images/account/pools.png"
              className="max-md:w-32 sm:w-50"
            />
            <H2 className="mb-3">{t('profile.poolsHistory.noPoolsYet')}</H2>
            <P className="text-center">
              {t('profile.poolsHistory.noPoolsYetDesc')}
            </P>
          </div>
        ) : (
          historyList()
        )}
      </div>
      {isPredictions && hasMorePredictions && (
        <Button
          variant="outline"
          className="w-full mt-5"
          onClick={onLoadMore}
          disabled={isLoadingPredictions}
        >
          {isLoadingPredictions ? (
            <Spinner variant="circle" size={16} />
          ) : (
            t('predictions.loadMore')
          )}
        </Button>
      )}
      {isPools && hasMorePoolTransactions && (
        <Button
          variant="outline"
          className="w-full mt-5"
          onClick={onLoadMorePoolTransactions}
          disabled={isLoadingPoolTransactions}
        >
          {isLoadingPoolTransactions ? (
            <Spinner variant="circle" size={16} />
          ) : (
            t('predictions.loadMore')
          )}
        </Button>
      )}
    </div>
  );
}
